import { utcDayKey } from "@/lib/calendarDay";

/**
 * R2b — Detect whether an elevated user is booking a past date within the billing window.
 *
 * Both MakeABookingDialog and RepresentativeBookingFlow/Confirmation use this guard; keeping it
 * in one place ensures the logic is consistent and only needs updating here if the billing-window
 * definition changes.
 *
 * Compares the two dates by their UTC calendar day. The calendar is UTC-anchored end to end (a
 * booking's stored `date` is UTC-midnight of the centre's day — see @/lib/calendarDay), so "today"
 * must be the UTC day too, not the viewer's browser-local day: a LOCAL day-floor put a traveller
 * west of UTC on the wrong calendar day near midnight (e.g. 22:30 UTC is still "yesterday" in
 * America/New_York), misclassifying a same-day booking as past. (Kept consistent with
 * countPastOccurrences.)
 *
 * @param isElevated  Whether the current user is an admin / Practice Manager.
 * @param date        The selected booking date (Date object).
 * @param today       Override for today's date (defaults to `new Date()`; injectable for tests).
 * @returns           true if the user is elevated AND the date's UTC day is strictly before today's.
 */
export function isPastDateBooking(
  isElevated: boolean,
  date: Date | null | undefined,
  today: Date = new Date(),
): boolean {
  if (!isElevated || !date) return false;
  return utcDayKey(date) < utcDayKey(today);
}
